/**
 * Extra page sections for the shared article reader (ArticleView), flogger
 * chapter-model migration PR-2c (design doc section 8, row 2c).
 *
 * A page can add sections that are not chapters — the flogger Rope section
 * and "Where you'll tie it" (PR-3) — placed before or after the chapters,
 * each with its own TOC entry and `#anchor`. They are never chapters: step
 * progress and chapter counts ignore them.
 *
 * Plain `.ts` so the client view and unit tests share it (no 'use client').
 */
import { slugifyAnchor } from './slugify-anchor'

export type ExtraSectionPlacement = 'before' | 'after'

/** The fields the ordering / anchor helpers need (the view adds `content`). */
export interface ExtraSectionLike {
  /** Stable, slug-safe anchor (`rope`, `where-youll-tie-it`): the section's
   *  DOM id and its `#hash`. */
  id: string
  /** Heading and TOC label (already localized). */
  tocLabel: string
  /** Relative to the chapters. */
  placement: ExtraSectionPlacement
}

/**
 * Anchors ArticleView itself gives fixed sections: the synthetic Overview
 * entry (`#overview`) and the server-built What's next / Comments entries
 * (their labels are English-only, so their slugs are always `whats-next` /
 * `comments`). An extra cannot take one, or that section's deep link would
 * silently move to `-2`.
 */
export const EXTRA_SECTION_RESERVED_ANCHORS: ReadonlySet<string> = new Set([
  'overview',
  'whats-next',
  'comments',
])

/** Internal scroll-spy / ref key for an extra section (never a chapter id). */
export function extraSectionKey(anchor: string): string {
  return `__extra:${anchor}`
}

export interface NormalizedExtraSection<E extends ExtraSectionLike> {
  /** Internal key (refs, scroll-spy, TOC entry id). */
  key: string
  /** DOM id + `#hash` (the slugified `id`). */
  anchor: string
  extra: E
}

/**
 * Validate and key the extras, in the caller's order. `id` is slugified (a
 * slug-safe id is unchanged); an extra whose anchor is empty, reserved
 * (`EXTRA_SECTION_RESERVED_ANCHORS`) or already used by an earlier extra is
 * dropped, so DOM ids and React keys stay unique.
 *
 * The reverse case is not caught here: an extra's id wins over a chapter's
 * explicit `anchorId` of the same value (that chapter moves to `-2`). Content
 * rendered next to known extras must keep its chapter anchors off those ids
 * when it is written; the flogger migration reserves them through
 * `FLOGGER_RESERVED_CHAPTER_ANCHORS` (flogger-content-model.ts).
 */
export function normalizeExtraSections<E extends ExtraSectionLike>(
  extras: readonly E[] | null | undefined,
): NormalizedExtraSection<E>[] {
  if (!extras || extras.length === 0) return []
  const seen = new Set<string>()
  const out: NormalizedExtraSection<E>[] = []
  for (const extra of extras) {
    if (!extra || (extra.placement !== 'before' && extra.placement !== 'after')) continue
    const anchor = slugifyAnchor(String(extra.id ?? ''))
    if (!anchor || EXTRA_SECTION_RESERVED_ANCHORS.has(anchor) || seen.has(anchor)) continue
    seen.add(anchor)
    out.push({ key: extraSectionKey(anchor), anchor, extra })
  }
  return out
}

export type OrderedEntry<S, E extends ExtraSectionLike> =
  | { type: 'section'; section: S }
  | { type: 'extra'; extra: NormalizedExtraSection<E> }

/**
 * Page order: the sections with the `before` extras inserted just before the
 * first chapter and the `after` extras just after the last chapter (so after
 * the chapters but before What's next / Comments). With no chapter both go
 * at the top of the list, `before` first. Each group keeps the caller's
 * order. With no extras the result is the sections, in order.
 */
export function orderSectionsWithExtras<S extends { kind: string }, E extends ExtraSectionLike>(
  sections: readonly S[],
  extras: readonly NormalizedExtraSection<E>[],
): OrderedEntry<S, E>[] {
  const asSections = sections.map((section) => ({ type: 'section' as const, section }))
  if (extras.length === 0) return asSections
  const before = extras
    .filter((e) => e.extra.placement === 'before')
    .map((extra) => ({ type: 'extra' as const, extra }))
  const after = extras
    .filter((e) => e.extra.placement === 'after')
    .map((extra) => ({ type: 'extra' as const, extra }))
  const first = sections.findIndex((s) => s.kind === 'chapter')
  if (first === -1) return [...before, ...after, ...asSections]
  let last = first
  sections.forEach((s, i) => {
    if (s.kind === 'chapter') last = i
  })
  return [
    ...asSections.slice(0, first),
    ...before,
    ...asSections.slice(first, last + 1),
    ...after,
    ...asSections.slice(last + 1),
  ]
}
